/* eslint-disable */
// VENDORED from learning_brain_poc/rhs4.js (W7b / F3, POC commit 6ad5075e). V5 changes, all marked 'V5':
// - the map is read from GET /api/v5/brain/diagmap (session, diagnosis, seq) through brainApi.js with the app's auth headers;
// - Questions tab lists tse:diag_state asked/pending (pending row -> onPendingQuestion, focuses the chat composer);
// - Details drops the dev-only Settings, /api/curve test and technical details; Stored data only when state.devMode;
// - tab changes fire onTabChange (the host remembers the last tab per user); CSS.escape fallback for jsdom.
import { brainGet, BRAIN_ENDPOINTS, BRAIN_NETWORK_FAILURE, describeBrainFailure } from './brainApi';
// Chat v4 right-hand side: six tabs (Diagnosis, Evidence, Questions, Learning, Graph, Details); in V5 over tse:diag_state (rhs4Adapter.js).
// window.Rhs4 = {render(state, callbacks), mount(el, callbacks), reset(), focus(tab, section)}; the evidence map (Diagnosis tab) is window.DiagMap fed by GET /api/v5/brain/diagmap.
// All dynamic text goes in as text nodes through cleanLabel(); developer text appears only inside collapsed 'Technical details' blocks.
(function () {
  'use strict';
  const HOST_ID = 'v4-rhs', TAB_KEY = 'rhs4.tab';
  const TABS = [{id: 'diagnosis', label: 'Diagnosis'}, {id: 'evidence', label: 'Evidence'}, {id: 'questions', label: 'Questions'}, {id: 'learning', label: 'Learning'}, {id: 'graph', label: 'Graph'}, {id: 'details', label: 'Details'}];
  const MAP_CACHE_SIZE = 4, HTTP_NOT_FOUND = 404, MAP_MISSING = Symbol('map-missing');
  const MAP_TEXT = {
    loading: 'Loading the map…', missing: 'The map is not available yet.', failed: 'The map could not be loaded.', retry: 'Try again',
    empty: 'Describe the problem in the chat. What was said and the possible causes it points to appear here.',
  };
  const CAP = 0.95, TOP_OPEN = 3, TOP_LEADS = 3, PATH_ANSWERS_SHOWN = 3, MAX_TURNS = 30, MAX_PARENT_HOPS = 20;
  const HOT_DAYS = 30, BOOSTS = [2, 3, 5, 10], DEFAULT_BOOST = 5;
  const K_STEP = 0.1, K_MAX = 100, MATRIX_ROWS = 6;
  const TEACH_BUSY = 'Closing the diagnosis and saving what was learned…';
  const TEACH_REFUSED = {
    not_in_diagnosis: 'There is no diagnosis in this chat to teach.',
    learning_disabled: 'Learning is off for this chat. Switch it on in the Learning tab to teach.',
    invalid_payload: 'This diagnosis was already taught or learned, so it cannot be taught again.',
  };
  const TEACH_REFUSED_FALLBACK = 'That could not be taught. Please try again.';
  const TEACH_EDITOR = {label: 'Open learning editor', hint: 'Check the symptoms and troubleshooting steps, and say what the root cause was and how it was fixed, mitigated or worked around.'};
  const DEFAULT_SETTINGS = {as_of_days: 0, k1: 0.4, k2: 0.6, batch: 0};
  const AS_OF_CHOICES = [[0, 'Today'], [90, 'In 3 months'], [180, 'In 6 months'], [365, 'In 12 months']];
  const BATCH_CHOICES = [[0, 'Several questions'], [1, 'One at a time']];
  const DECIDED = new Set(['root_cause', 'propose']);
  const ASKING = 'Asking questions';
  const PHASE_WORDS = {CLARIFY: ASKING, EVALUATE: ASKING, BACK: ASKING, DIAG_STEP: 'Testing a possible cause',
    LIKELY_CAUSE: 'Possible cause found', ROOT_CAUSE: 'Root cause identified', HANDOFF: 'No cause left'};
  // V5: 'scope' = the cause does not apply to where the affected users are or what the case runs on (scope gate)
  const REASON_TEXT = {fix_failed: 'Its fix did not resolve the problem.', set_aside: 'Set aside when a round ran out of questions. It was not ruled out.',
    scope: 'It does not apply to this case: it needs a different place or setup than the one this case has.'};
  const RULED_TITLE = 'Ruled out', ASIDE_TITLE = 'Set aside', NO_RULED = 'No cause has been ruled out yet.', NO_ASIDE = 'No cause has been set aside.';
  const ASIDE_NOTE = 'Not tested and not ruled out: a round ran out of questions. Use Investigate this cause to look at one again.';
  const FOLDABLE_TABS = new Set(['questions', 'details']), COLLAPSE_ALL = 'Collapse all', SHOW_ALL = 'Show all';
  const LEARNING_OFF = 'Learning is off', TECH_TITLE = 'Technical details', FALLBACK_ANSWER = 'An answer from the chat', OTHER_TOPIC = 'Other questions';
  const MARK = {done: '✓', failed: '✗', current: '▶', pending: '○'};
  const MARK_WORD = {done: 'Confirmed', failed: 'Not confirmed', current: 'Checking now', pending: 'Not checked yet'};
  const HOT_TIP = 'In season causes are weighed higher for ' + HOT_DAYS + ' days. Nothing is learned from it.';
  const DEFAULT_TAB = 'diagnosis', TRUST_EPS = 0.005, HOT_MARK = '🔥';
  const TAG_LEADING = /^\s*\[[^\]]*runbook\]\s*/i;
  const RUNBOOK_WORD = /\brunbook\b/gi, GUIDE_WORD = 'guide';
  const RAW_KEY = /^[a-z0-9_]+(?:\.[a-z0-9_]+)+$/;
  const RAW_ID = /^(?:(?:SYM|F|PAT|Q)_\w+|(?:STEP|RC):\S+)$/;
  const DEV_WORDS = /\b(?:runbook|graph|node|pattern|trust|tool|planner|lambda|cdg\w*|phase|bolt|engine|template)/i;
  const TRACE_RECORDED = /^answer: \w+ to '.*' -> recorded '(.*)'$/;
  const TRACE_CONFIDENCE = /^confidence \(relative to open causes\): (.*)$/;
  const TRACE_NO_SHORTCUT = /^shortcut check: .*not enough/;
  const TRACE_SOURCES = /(\d+) of (\d+) source\(s\) cited, (\d+) image\(s\) shown/;

  let host = null, callbacks = {}, state = {}, built = false;
  let tab = readStore(TAB_KEY, DEFAULT_TAB), hotBoost = DEFAULT_BOOST;
  const openState = {}, dirty = new Set(), panels = {}, tabButtons = {}, mapCache = new Map();
  let neoHost = null, neoLoaded = false, ixTree = null, ixCache = null;
  let mapHost = null, mapLast = null, mapShow, mapSeq = 0;
  const trustSeen = {}, trustDelta = {};
  let turnLog = [], turnSig = null, turnCount = 0, prevObserved = 0;

  // ---------- small helpers ----------
  function readStore(key, fallback) { try { return window.sessionStorage.getItem(key) || fallback; } catch (e) { return fallback; } }
  function writeStore(key, value) { try { window.sessionStorage.setItem(key, value); } catch (e) { /* storage blocked: the choice lasts for this page only */ } }
  function h(tag, props, ...kids) {
    const e = document.createElement(tag);
    Object.entries(props || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === false) return;
      if (k === 'class') e.className = v;
      else if (k.startsWith('on') && typeof v === 'function') e.addEventListener(k.slice(2), v);
      else e.setAttribute(k, v === true ? '' : String(v));
    });
    kids.flat(Infinity).forEach(c => {
      if (c === null || c === undefined || c === false) return;
      e.append(c instanceof Node ? c : document.createTextNode(String(c)));
    });
    return e;
  }
  const pct = x => Math.round(Math.min(Number(x) || 0, CAP) * 100);
  const plural = (n, word) => n + ' ' + word + (n === 1 ? '' : 's');
  const muted = text => h('p', {class: 'v4-muted'}, text);
  const fire = (name, ...args) => { if (typeof callbacks[name] === 'function') callbacks[name](...args); };
  const last = () => state.last || {};
  const cards = () => last().cards || state.cards || [];
  const plan = () => last().plan || {};
  const decision = () => last().decision || plan().decision || null;
  const pending = () => last().pending || state.pending || [];
  const currentStep = () => last().step || state.step || null;
  const ruledIds = () => new Set(last().ruled_out || state.ruled_out || []);
  const causeInfo = pid => (state.causes || []).find(c => c.pid === pid) || {};
  const learningOn = () => !state.learning || state.learning.enabled !== false;
  const graphNode = nid => ((state.graph && state.graph.nodes) || []).find(x => x.nid === nid);
  const isSymptom = nid => { const n = graphNode(nid); return n ? n.type === 'symptom' : /^SYM_/.test(String(nid)); };

  // ---------- clean labels ----------
  function treeIndex() {
    if (ixTree === state.tree && ixCache) return ixCache;
    const ix = {qByKey: {}, factQ: {}, factLabel: {}, parentOf: {}};
    ((state.tree && state.tree.questions) || []).forEach(q => {
      ix.qByKey[q.key] = q;
      (q.answers || []).forEach(a => { ix.factQ[a.nid] = q.key; ix.factLabel[a.nid] = a.label; });
    });
    ((state.tree && state.tree.edges) || []).forEach(e => { if (!(e.key in ix.parentOf)) ix.parentOf[e.key] = e; });
    ixTree = state.tree;
    return (ixCache = ix);
  }
  const allSteps = () => [...cards().flatMap(c => c.steps || []), ...((state.diag && state.diag.steps) || []), ...(currentStep() ? [currentStep()] : [])];
  function keyText(key) {
    const q = treeIndex().qByKey[key];
    const step = allSteps().find(s => s.key === key && s.text);
    const row = (plan().question_table || []).find(r => r.key === key);
    const raw = (q && q.hint) || (step && step.text) || (row && row.hint) || '';
    return RAW_KEY.test(raw) ? '' : cleanLabel(raw);
  }
  function idText(nid) {
    const n = graphNode(nid);
    const raw = (n && n.label) || treeIndex().factLabel[nid] || '';
    return RAW_ID.test(raw) ? '' : cleanLabel(raw);
  }
  function cleanLabel(text) {
    let s = String(text === null || text === undefined ? '' : text).trim();
    if (RAW_KEY.test(s)) return keyText(s);
    if (RAW_ID.test(s)) return idText(s);
    s = s.replace(TAG_LEADING, '').replace(RUNBOOK_WORD, GUIDE_WORD);
    return window.DiagMapLayout ? window.DiagMapLayout.scrub(s) : s.trim();
  }
  const nodeLabel = nid => idText(nid) || FALLBACK_ANSWER;
  function section(key, title, body, opts) {
    const o = opts || {};
    const d = h('details', {class: 'v4-section' + (o.cls ? ' ' + o.cls : ''), 'data-key': key},
      h('summary', null, h('span', {class: 'v4-section-title'}, title), o.count !== undefined ? h('span', {class: 'v4-count'}, String(o.count)) : null),
      h('div', {class: 'v4-section-body'}, body));
    d.open = key in openState ? openState[key] : !!o.open;
    d.addEventListener('toggle', () => { openState[key] = d.open; if (o.onOpen && d.open) o.onOpen(); });
    return d;
  }
  function seg(items, current, label, onPick) {
    return h('div', {class: 'v4-seg', role: 'group', 'aria-label': label}, items.map(([value, text]) => {
      const on = String(value) === String(current);
      return h('button', {type: 'button', class: on ? 'on' : '', 'aria-pressed': String(on), onclick: () => onPick(value)}, text);
    }));
  }

  // ---------- skeleton and tabs ----------
  function build() {
    host.classList.add('v4-rhs');
    host.textContent = '';
    const list = h('div', {class: 'v4-tabs', role: 'tablist', 'aria-label': 'Diagnosis views'});
    TABS.forEach(t => {
      const b = h('button', {type: 'button', class: 'v4-tab', role: 'tab', id: 'v4-tab-' + t.id, 'data-tab': t.id,
        'aria-controls': 'v4-panel-' + t.id, onclick: () => selectTab(t.id, false)}, t.label);
      tabButtons[t.id] = b;
      list.append(b);
      panels[t.id] = h('div', {class: 'v4-panel', role: 'tabpanel', id: 'v4-panel-' + t.id, 'aria-labelledby': 'v4-tab-' + t.id, tabindex: '0'});
    });
    list.addEventListener('keydown', onTabKey);
    host.append(list, ...TABS.map(t => panels[t.id]));
    neoHost = h('div', {class: 'v4-neo'});
    built = true;
    if (!TABS.some(t => t.id === tab)) tab = DEFAULT_TAB;
    applyTab();
  }
  function onTabKey(e) {
    const ids = TABS.map(t => t.id), i = ids.indexOf(tab);
    const next = {ArrowRight: (i + 1) % ids.length, ArrowLeft: (i - 1 + ids.length) % ids.length, Home: 0, End: ids.length - 1}[e.key];
    if (next === undefined) return;
    e.preventDefault(); selectTab(ids[next], true);
  }
  function selectTab(id, moveFocus) {
    tab = id; writeStore(TAB_KEY, id); applyTab(); fire('onTabChange', id);
    if (moveFocus) tabButtons[id].focus();
  }
  function applyTab() {
    TABS.forEach(t => {
      const on = t.id === tab;
      tabButtons[t.id].setAttribute('aria-selected', String(on));
      tabButtons[t.id].tabIndex = on ? 0 : -1;
      tabButtons[t.id].classList.toggle('on', on);
      panels[t.id].hidden = !on;
    });
    if (dirty.has(tab)) renderPanel(tab);
  }
  function renderPanel(id) {
    dirty.delete(id);
    if (id === 'diagnosis') dropMap();
    const fn = {diagnosis: renderDiagnosis, evidence: renderEvidence, questions: renderQuestions, learning: renderLearning, graph: renderGraph, details: renderDetails}[id];
    const body = fn();
    panels[id].replaceChildren(...(FOLDABLE_TABS.has(id) ? [foldBar(panels[id]), body] : [body]));
  }
  // Collapse all / Show all for tabs made of foldable sections
  function foldBar(panel) {
    const setAll = open => panel.querySelectorAll('details.v4-section').forEach(d => { if (d.open !== open) d.open = open; });
    return h('div', {class: 'v4-foldbar'},
      h('button', {type: 'button', class: 'v4-fold', onclick: () => setAll(false)}, COLLAPSE_ALL),
      h('button', {type: 'button', class: 'v4-fold', onclick: () => setAll(true)}, SHOW_ALL));
  }

  // ---------- the graph: the clues read, the causes they point to, and what a Fixed click would write (nodegraph.js) ----------
  function renderGraph() {
    if (!window.NodeGraph) return muted('The graph picture is not available.');
    return h('div', {class: 'v4-graphtab'},
      h('p', {class: 'v4-muted'}, 'What is inside Neo4j for this chat: the clues Arta read, the causes they point to, and what a Fixed click would write. It updates after every answer.'),
      window.NodeGraph.live(state),
      h('p', {class: 'v4-muted'}, h('a', {href: '/learn', target: '_blank', rel: 'noopener'}, 'How Arta learns, before and after')));
  }

  // ---------- 1. Diagnosis ----------
  function statusWord() {
    const word = baseStatusWord(), n = Number(state.round || 0);
    return n > 0 && !state.escalated && !state.resolved ? word + ' · Round ' + (n + 1) : word;
  }
  function baseStatusWord() {
    if (state.escalated) return 'Escalated';
    if (state.resolved) return 'Resolved';
    if (state.awaitingFix) return 'Waiting for your result';
    const ph = plan().phase, dec = decision();
    if (dec === 'propose') return PHASE_WORDS.LIKELY_CAUSE;
    if (dec === 'handoff' && !state.diag) return PHASE_WORDS.HANDOFF;
    return PHASE_WORDS[ph] || (state.observed && state.observed.length ? ASKING : 'Waiting for the problem');
  }
  function stepPosition(d) {
    const steps = d.steps || [], n = steps.length;
    const cur = steps.findIndex(s => s.status === 'current');
    if (cur >= 0) return {k: cur + 1, n, started: true};
    const doneCount = steps.filter(s => s.status === 'done' || s.status === 'failed').length;
    return {k: doneCount, n, started: doneCount > 0};
  }
  function stepFinding(pid, s) {
    const card = cards().find(c => c.pid === pid) || {};
    const full = (card.steps || []).find(x => x.key === s.key) || {};
    if (s.status === 'done' && full.confirm_fact) return nodeLabel(full.confirm_fact);
    if (s.status === 'failed' && full.deny_fact) return nodeLabel(full.deny_fact);
    return MARK_WORD[s.status] || '';
  }
  function renderDiagnosis() {
    const wrap = h('div', {class: 'v4-diag'});
    const head = h('div', {class: 'v4-status'}, h('span', {class: 'v4-status-word'}, statusWord()));
    if (!learningOn()) {
      head.append(h('button', {type: 'button', class: 'v4-tag v4-tag-off', title: 'Open the Learning tab to switch learning on.',
        onclick: () => focusSection('learning')}, LEARNING_OFF));
    }
    wrap.append(head);
    if (!state.observed || !state.observed.length) {
      wrap.append(muted('Describe the problem in the chat. The possible cause, its checks and the other causes appear here.'));
      const early = teachCard();
      if (early) wrap.append(early);
      return wrap;
    }
    wrap.append(...[pathLine(), branchesSection(), factsSection(), notedSection()].filter(Boolean));
    const d = state.diag;
    if (d) wrap.append(causeCard(d));
    else if (!cards().length) wrap.append(muted('No possible cause matches what was said so far.'));
    wrap.append(...[h('div', {class: 'v4-diag-map', 'data-key': 'map'}, h('h3', {class: 'v4-h'}, 'Evidence map', lodTag()), lodStrip(), mapView()), causesList(), ruledOutSection(), setAsideSection()].filter(Boolean));      // append(null) would print the word null
    const teach = teachCard();
    if (teach) wrap.append(teach);
    return wrap;
  }
  // line of diagnosis bar above the evidence map and its tag in the map heading (rhs4_lod.js); null unless /api/info says lod.enabled
  const lodDeps = () => ({h, cleanLabel, fire, cards, ruledIds});
  function lodStrip() { return window.Rhs4Lod ? window.Rhs4Lod.render(state, lodDeps()) : null; }
  function lodTag() { return window.Rhs4Lod ? window.Rhs4Lod.tag(state, lodDeps()) : null; }
  // two or more symptoms in the message: how much of the open possible causes each one holds, and which are ruled out
  function branchesSection() {
    const rows = (state.branches || []).slice().sort((a, b) => (a.status === 'disbanded') - (b.status === 'disbanded') || b.mass - a.mass);
    if (rows.length < 2) return null;
    const live = rows.filter(b => b.status !== 'disbanded').length;
    const body = h('div', null, h('p', {class: 'v4-muted'}, 'Each symptom you gave is checked in parallel. A cause can belong to two, so the shares can add up to more than 100%. A symptom is dropped when every cause for it is ruled out.'),
      h('ul', {class: 'v4-branches'}, rows.map(b => h('li', {class: 'v4-branch ' + b.status},
        h('span', {class: 'v4-branch-name'}, b.label),
        h('span', {class: 'v4-branch-bar', 'aria-hidden': 'true'}, h('span', {style: 'width:' + Math.round(Math.min(b.mass, 1) * 100) + '%'})),
        h('span', {class: 'v4-branch-pct'}, b.status === 'disbanded' ? 'no cause left' : Math.round(b.mass * 100) + '%'),
        h('span', {class: 'v4-branch-open'}, b.open + ' of ' + plural(b.total, 'cause'))))));
    return section('branches', 'Symptoms being checked', body, {count: live + ' of ' + rows.length, open: true});
  }
  // every symptom and answer taken as evidence so far, in the order given
  function factsSection() {
    const obs = state.observed || [];
    const body = h('ul', {class: 'v4-facts'}, obs.map(n => {
      const sym = isSymptom(n);
      return h('li', null, h('span', {class: 'v4-fact-kind' + (sym ? ' sym' : '')}, sym ? 'Symptom' : 'Answer'), h('span', null, nodeLabel(n)));
    }));
    return section('facts', 'Facts gathered', body, {count: obs.length, open: true});
  }
  // V5: statements the catalogue cannot hold, kept as context only (never evidence); hedged ones are tagged unsure
  function notedSection() {
    const notes = state.noted || [];
    if (!notes.length) return null;
    const body = h('ul', {class: 'v4-facts v4-noted'}, notes.map(n => h('li', null,
      h('span', {class: 'v4-fact-kind v4-noted-kind'}, 'Noted'),
      h('span', null, n.text, h('span', {class: 'v4-noted-quote', title: 'Your words'}, ' \u201c' + n.quote + '\u201d'),
        n.hedged ? h('span', {class: 'v4-tag v4-noted-unsure', title: 'You were not sure about this'}, 'unsure') : null))));
    return section('noted', 'Also noted', body, {count: notes.length, open: true});
  }
  function pathLine() {
    const obs = state.observed || [], d = state.diag;
    const syms = obs.filter(isSymptom), answers = obs.filter(n => !isSymptom(n));
    const leader = d ? cleanLabel(d.cause) : cleanLabel((cards().find(c => !c.excluded) || {}).label);
    const pos = d ? stepPosition(d) : null;
    const parts = [];
    syms.forEach(s => parts.push({text: nodeLabel(s), on: true, cls: 'sym'}));
    const hiddenCount = Math.max(answers.length - PATH_ANSWERS_SHOWN, 0);
    if (hiddenCount) parts.push({text: plural(hiddenCount, 'earlier answer'), on: true, title: answers.slice(0, hiddenCount).map(nodeLabel).join('\n')});
    answers.slice(hiddenCount).forEach(a => parts.push({text: nodeLabel(a), on: true}));
    parts.push({text: leader || 'Possible cause', on: !!leader, cls: 'cause'});
    if (pos && pos.n) parts.push(pos.started ? {text: 'Step ' + pos.k + ' of ' + pos.n, on: true} : {text: 'Checks', on: false});
    parts.push({text: 'Root cause', on: !!(d && d.identified)});
    parts.push({text: d && d.fix_kind === 'escalation' ? 'Escalation' : 'Fix', on: !!(d && d.identified)});
    return h('ol', {class: 'v4-path', 'aria-label': 'How the diagnosis got here'},
      parts.map((p, i) => h('li', {class: (p.on ? 'on ' : '') + (p.cls || ''), title: p.title || null},
        i ? h('span', {class: 'v4-sep', 'aria-hidden': 'true'}, '›') : null, h('span', null, p.text))));
  }
  function causeCard(d) {
    const pos = stepPosition(d), name = cleanLabel(d.cause);
    const card = h('div', {class: 'v4-cause-card' + (d.identified ? ' found' : '')});
    card.append(h('div', {class: 'v4-cause-top'},
      h('div', null, h('div', {class: 'v4-label'}, d.identified ? 'Root cause' : 'Possible cause'), causeName(d.pid, name, 'v4-cause-name'))));
    const meta = [];
    if (d.confidence !== null && d.confidence !== undefined) meta.push('Confidence ' + pct(d.confidence) + '%');
    if (pos.n) meta.push(d.identified ? 'All ' + pos.n + ' checks done' : pos.started ? 'Step ' + pos.k + ' of ' + pos.n : plural(pos.n, 'check') + ' after the questions');
    card.append(h('div', {class: 'v4-cause-meta'}, meta.join(' · ')));      // no In season tag (21 Oct call)
    const steps = d.steps || [];
    card.append(steps.length
      ? h('ol', {class: 'v4-steps'}, steps.map(s => h('li', {class: 'v4-step v4-step-' + s.status},
        h('span', {class: 'v4-mark', 'aria-label': MARK_WORD[s.status] || MARK_WORD.pending}, MARK[s.status] || MARK.pending),
        h('div', null, h('div', {class: 'v4-step-text'}, cleanLabel(s.text)), h('div', {class: 'v4-step-find'}, stepFinding(d.pid, s))))))
      : muted('No checks are stored for this cause.'));
    if (d.identified) {
      const what = d.fix_kind === 'escalation' ? 'Escalation' : 'Fix';
      card.append(h('div', {class: 'v4-fix'}, h('div', {class: 'v4-label'}, what), h('div', null, cleanLabel(d.fix))));
    } else if (d.fix) {
      card.append(h('div', {class: 'v4-fix pending'}, h('div', {class: 'v4-label'}, 'Fix if confirmed'), h('div', null, cleanLabel(d.fix))));
    }
    card.append(trackRecord(d.pid));
    return card;
  }
  // the hotline state shown is the server's latest answer (/api/hotline or /api/causes); a card's copy is only a fallback
  const hotOn = pid => { const i = causeInfo(pid), c = cards().find(x => x.pid === pid) || {}; return typeof i.hotline_on === 'boolean' ? i.hotline_on : !!(c.hotline_on || c.hotline); };
  function hotTag(pid) {
    if (!hotOn(pid)) return null;
    const info = causeInfo(pid), card = cards().find(c => c.pid === pid) || {};
    const boost = info.hotline_boost || card.hotline_boost, until = info.hotline_until || card.hotline_until;
    return h('span', {class: 'v4-tag v4-tag-hot'}, (boost ? 'x' + Number(boost) : 'In season') + (until ? ' until ' + until : ''));
  }
  function trackRecord(pid) {
    const info = causeInfo(pid), card = cards().find(c => c.pid === pid) || {};
    const score = info.trust !== undefined ? info.trust : card.trust;
    const ok = info.cases !== undefined ? info.cases : (card.cases || 0);
    const wrong = info.wrong !== undefined ? info.wrong : (card.wrong || 0);
    const body = h('div', {class: 'v4-track'},
      h('div', null, 'Confirmed ' + plural(ok, 'time') + ', wrong ' + plural(wrong, 'time') + '.'),
      score !== undefined && score !== null ? h('div', null, 'Track record score ' + Number(score).toFixed(2) + '. It rises with each taught conversation and falls with wrong diagnoses and time.') : null);
    return section('track-' + pid, 'Track record', body, {cls: 'v4-mini'});
  }
  function ruledOutReason(c) {
    const why = (state.ruledReason || {})[c.pid];
    if (REASON_TEXT[why]) return REASON_TEXT[why];
    const kill = (c.kills || [])[0];
    return (kill && cleanLabel(kill.why)) || 'Its check did not confirm it in this chat.';
  }
  const isAside = pid => (state.ruledReason || {})[pid] === 'set_aside';
  // names and reasons only, never ids; 'Ruled out' holds causes a check or a failed fix removed, 'Set aside' the ones a round only left untested
  function ruledRows(wantAside) {
    const all = cards(), known = new Set(all.map(c => c.pid)), ruledSet = ruledIds();
    return [...all.filter(c => c.excluded || ruledSet.has(c.pid)), ...[...ruledSet].filter(p => !known.has(p)).map(p => Object.assign({}, causeInfo(p), {pid: p}))]
      .filter(c => isAside(c.pid) === wantAside)
      .map(c => ({name: cleanLabel(c.label) || idText(c.pid), why: ruledOutReason(c)})).filter(r => r.name);
  }
  function ruledOutSection() {
    const rows = ruledRows(false);
    const body = rows.length ? h('ul', {class: 'v4-ruled'}, rows.map(r => h('li', null, h('b', null, r.name), h('div', {class: 'v4-muted'}, r.why)))) : muted(NO_RULED);
    const d = section('ruled-out', RULED_TITLE, body, {count: rows.length});
    d.open = rows.length > 0;
    return d;
  }
  function setAsideSection() {
    const rows = ruledRows(true);
    if (!rows.length) return null;
    return section('set-aside', ASIDE_TITLE, h('div', null, muted(ASIDE_NOTE), h('ul', {class: 'v4-ruled'}, rows.map(r => h('li', null, h('b', null, r.name))))), {count: rows.length});
  }
  const checksDeps = () => ({h, cleanLabel, section, muted, fire, pct, plural});
  function checksSection() { return window.Rhs4Checks ? window.Rhs4Checks.render(state, checksDeps()) : null; }
  function teachCard() {
    if (!learningOn() || !state.mapKey) return null;       // always there while a diagnosis is on screen, not only once a cause is decided
    const hand = state.handoff || {};
    const card = h('div', {class: 'v4-teach', 'data-key': 'teach', tabindex: '-1'}, h('h3', {class: 'v4-h'}, 'Teach this conversation'));
    if (state.taught) {
      card.append(muted('This conversation has been taught. Start a new chat to teach another one.'));
      return card;
    }
    if (state.teachPending) {
      card.append(h('p', {class: 'v4-teach-busy', role: 'status', 'aria-live': 'polite'}, h('span', {class: 'v4-spinner', 'aria-hidden': 'true'}), ' ', TEACH_BUSY));
      return card;
    }
    if (state.teachRefused) card.append(h('p', {class: 'v4-teach-refused', role: 'alert'}, TEACH_REFUSED[state.teachRefused] || TEACH_REFUSED_FALLBACK));
    card.append(h('p', null, TEACH_EDITOR.hint), h('div', {class: 'v4-actions'},
      h('button', {type: 'button', class: 'primary', onclick: () => fire('onOpenEditor')}, TEACH_EDITOR.label)));
    const steps = hand.steps || [];
    if (steps.length) {
      card.append(section('guided', 'Guided steps', h('ul', {class: 'v4-guided'}, steps.map(s => h('li', {class: s.done ? 'done' : ''},
        h('span', {class: 'v4-mark', 'aria-label': s.done ? 'Done' : 'Not done'}, s.done ? MARK.done : MARK.pending), ' ', cleanLabel(s.text)))), {count: steps.length}));
    }
    return card;
  }

  // ---------- 2. Evidence ----------
  function renderEvidence() {
    return h('div', {class: 'v4-evidence'}, matrixView());
  }
  function noteTrust() {
    (state.causes || []).forEach(c => {
      if (typeof c.trust !== 'number') return;
      const prev = trustSeen[c.pid];
      if (prev !== undefined && Math.abs(c.trust - prev) >= TRUST_EPS) trustDelta[c.pid] = c.trust - prev;
      trustSeen[c.pid] = c.trust;
    });
  }
  function trackLine(pid) {
    const info = causeInfo(pid), card = cards().find(c => c.pid === pid) || {};
    const score = info.trust !== undefined ? info.trust : card.trust, delta = trustDelta[pid];
    if (typeof score !== 'number') return null;
    return h('div', {class: 'v4-track'}, 'Track record ' + score.toFixed(2),
      delta ? h('span', {class: delta > 0 ? 'v4-up' : 'v4-down'}, (delta > 0 ? ' ▲ +' : ' ▼ ') + delta.toFixed(2) + ' after teaching') : null);
  }
  // the cause name opens its write-up (rhs4_causedetail.js); without that file it stays plain text
  const causeDetailDeps = () => ({h, cleanLabel, muted, pct, plural, label: nodeLabel, causeInfo, cards, ruledIds, learningOn, state: () => state, explainDeps});
  const causeName = (pid, name, cls) => (pid && window.Rhs4CauseDetail ? window.Rhs4CauseDetail.nameTrigger(pid, name, cls, causeDetailDeps()) : h('div', {class: cls}, name));
  const explainDeps = () => ({h, cleanLabel, muted, pct, plural, causeInfo, cap: CAP, focus: focusSection, trustDelta: pid => trustDelta[pid], learningOn,
    kNow: () => (typeof plan().k1 === 'number' ?{k1: plan().k1, k2: plan().k2} : settingsNow())});
  function evidenceRow(c, ruled) {
    // the In season flame and its tag are not rendered (21 Oct call: they skew the percentages); hotOn/hotTag and /api/hotline stay for the learning editor
    const p = pct(c.post), name = cleanLabel(c.label) || idText(c.pid) || 'A possible cause', X = window.Rhs4Explain;
    const bar = h('div', {class: 'v4-bar', role: 'img', 'aria-label': 'Confidence ' + p + '%'}, h('i', {style: 'width:' + p + '%'}));
    const pctEl = X && !ruled ? X.pctTrigger(c, explainDeps()) : h('span', {class: 'v4-pct'}, p + '%');   // hover, focus or tap explains how the % was reached
    return h('li', {class: 'v4-cause-row' + (ruled ? ' v4-ruled-row' : '')},
      h('div', {class: 'v4-row-head'}, causeName(c.pid, name, 'v4-row-name'),
        ruled ? h('span', {class: 'v4-tag v4-tag-ruled'}, isAside(c.pid) ? '○ Set aside' : '✗ Ruled out') : pctEl),
      ruled ? h('div', {class: 'v4-reason'}, ruledOutReason(c)) : X ? X.barHover(bar, pctEl) : bar,
      X && !ruled ? X.trackTrigger(trackLine(c.pid), c, explainDeps()) : trackLine(c.pid));
  }
  function causesList() {
    const all = cards(), ruledSet = ruledIds(), known = new Set(all.map(c => c.pid));
    const rows = [...all, ...[...ruledSet].filter(p => !known.has(p)).map(p => Object.assign({}, causeInfo(p), {pid: p, excluded: true}))];
    // no 'In season strength' select here (21 Oct call); the hotline feature returns in the learning editor
    return h('div', {class: 'v4-causes', 'data-key': 'causes'}, h('h3', {class: 'v4-h'}, 'Causes'),
      rows.length ? h('ul', {class: 'v4-cause-list'}, rows.map(c => evidenceRow(c, !!c.excluded || ruledSet.has(c.pid))))
        : muted('The possible causes appear here once the problem is described.'));
  }
  function matrixCell(c, nid) {
    const kill = (c.kills || []).find(k => k.nid === nid), edge = (c.matched || []).find(m => m.nid === nid);
    if (kill) return h('td', {class: 'v4-refute', title: cleanLabel(kill.why) || null}, '✗ rules out');
    if (edge && edge.seen === 0) return h('td', {class: 'v4-support', title: 'Listed in the documented checks for this cause'}, '✓ expected');
    if (edge && edge.wins > 0) return h('td', {class: 'v4-support', title: 'Seen in ' + edge.seen + ' past conversations, this was the cause in ' + edge.wins}, '✓ ' + edge.wins + ' of ' + edge.seen);
    if (edge) return h('td', {class: 'v4-weak', title: 'Seen in ' + edge.seen + ' past conversations, never the cause'}, '– never the cause');
    return h('td', {class: 'v4-none'}, h('span', {'aria-label': 'No link'}, '·'));
  }
  function matrixView() {
    const ruledSet = ruledIds();
    const open = cards().filter(c => !c.excluded && !ruledSet.has(c.pid));
    const obs = state.observed || [];
    if (!open.length || !obs.length) return muted('Answer a question to see what supports or rules out each open cause.');
    const head = h('tr', null, h('th', {scope: 'col', class: 'v4-rowh'}, 'What was said'),
      open.map(c => h('th', {scope: 'col'}, cleanLabel(c.label), h('div', {class: 'v4-muted'}, pct(c.post) + '%'))));
    const rows = obs.map(nid => h('tr', null, h('th', {scope: 'row', class: 'v4-rowh'}, nodeLabel(nid)), open.map(c => matrixCell(c, nid))));
    const ruled = cards().filter(c => c.excluded || ruledSet.has(c.pid));
    return h('div', null,
      h('p', {class: 'v4-lead'}, 'What supports or rules out each open cause.'),
      h('div', {class: 'v4-scroll'}, h('table', {class: 'v4-matrix'}, h('thead', null, head), h('tbody', null, rows))),
      muted('✓ 2 of 5 means the answer was seen in 5 past conversations and this was the cause in 2. ✗ means the answer rules the cause out.'),
      ruled.length ? muted('Ruled out: ' + ruled.map(c => cleanLabel(c.label)).join(', ') + '.') : null);
  }
  // V5: GET /api/v5/brain/diagmap?session_id=&diagnosis_id=&seq= (the server builds the POC request from the persisted snapshot)
  const mapBody = () => (state.mapKey && (state.observed || []).length ? state.mapKey : null);
  const rememberShow = v => { mapShow = !!v; };
  const mapOptions = () => ({showUnreached: mapShow, onToggle: rememberShow, onToggleUnreached: rememberShow,
    onOverride: state.canOverride ? pid => fire('onOverride', pid) : null, overridePid: state.overridePid || '',
    causeHover: window.Rhs4CauseDetail ? (node, pid, name) => window.Rhs4CauseDetail.mapTrigger(node, pid, name, causeDetailDeps()) : null,
    // every other node: a short popover (label + one line) whose Details button shows all its tooltip lines
    nodeHover: window.Rhs4Explain && window.Rhs4Explain.nodeTrigger ? (node, n, lines) => window.Rhs4Explain.nodeTrigger(node, n, lines, explainDeps()) : null});
  function paintMap(box, data) {
    Promise.resolve().then(() => {
      if (box !== mapHost || !box.isConnected) return;
      box.removeAttribute('aria-busy');
      box.replaceChildren();
      window.DiagMap.render(box, data, mapOptions());
    });
  }
  function mapNote(box, text, retry) {
    box.removeAttribute('aria-busy');
    box.replaceChildren(muted(text));
    if (retry) box.append(h('button', {type: 'button', class: 'v4-small', onclick: retry}, MAP_TEXT.retry));
  }
  function rememberMap(key, data, latest) {
    mapCache.set(key, data);
    if (latest) mapLast = data;
    while (mapCache.size > MAP_CACHE_SIZE) mapCache.delete(mapCache.keys().next().value);
  }
  // a map response is applied only when it answers the latest map request (a late one never replaces a newer map)
  function loadMap(box, key) {
    const n = ++mapSeq;
    box.setAttribute('aria-busy', 'true');
    box.replaceChildren(muted(MAP_TEXT.loading));
    let why = describeBrainFailure(BRAIN_NETWORK_FAILURE, null);
    brainGet(BRAIN_ENDPOINTS.DIAGMAP, JSON.parse(key))
      .then(r => {
        if (r.status === HTTP_NOT_FOUND) return MAP_MISSING;
        if (!r.ok || !r.data || r.data.kind !== 'diagmap') {
          why = describeBrainFailure(r.ok ? 500 : r.status, r.data);
          throw new Error('The map request failed with status ' + r.status);
        }
        return r.data;
      })
      .then(data => {
        if (data !== MAP_MISSING) rememberMap(key, data, n === mapSeq);
        if (n !== mapSeq || box !== mapHost || !box.isConnected) return;
        if (data === MAP_MISSING) mapNote(box, MAP_TEXT.missing);
        else paintMap(box, data);
      })
      .catch(() => {
        if (n === mapSeq && box === mapHost && box.isConnected) mapNote(box, MAP_TEXT.failed + ' Reason: ' + why, () => loadMap(box, key));
      });
  }
  function mapView() {
    if (!window.DiagMap || typeof window.DiagMap.render !== 'function') return h('div', {class: 'v4-map'}, muted(MAP_TEXT.missing));
    const box = h('div', {class: 'v4-map'});
    mapHost = box;
    const body = mapBody();
    if (!body) { if (mapLast && (state.observed || []).length) paintMap(box, mapLast); else box.append(muted(MAP_TEXT.empty)); return box; }
    const key = JSON.stringify(body);
    if (mapCache.has(key)) { mapSeq++; paintMap(box, (mapLast = mapCache.get(key))); }
    else loadMap(box, key);
    return box;
  }
  function dropMap() {
    if (mapHost && window.DiagMap && typeof window.DiagMap.destroy === 'function') window.DiagMap.destroy(mapHost);
    mapHost = null;
  }

  // ---------- 3. Questions ----------
  function nowKeys() {
    const ix = treeIndex(), keys = new Set((last().questions || []).map(q => q.key));
    pending().forEach(n => { if (ix.factQ[n]) keys.add(ix.factQ[n]); });
    return keys;
  }
  function askedKeys() {
    const ix = treeIndex(), keys = [];
    const add = k => { if (k && ix.qByKey[k] && !keys.includes(k)) keys.push(k); };
    (state.asked || []).forEach(n => add(ix.factQ[n]));
    (state.observed || []).forEach(n => add(ix.factQ[n]));
    (last().asked_log || []).forEach(a => add(a.key));
    return keys;
  }
  function stepForFact(nid) {
    const s = allSteps().find(x => x.confirm_fact === nid || x.deny_fact === nid);
    return s ? cleanLabel(s.text) : '';
  }
  function askedRow(question, answer, now) {
    return h('li', {class: 'v4-qrow' + (now ? ' v4-now' : '')},
      h('div', {class: 'v4-q'}, question, now ? h('span', {class: 'v4-tag'}, 'Asking now') : null),
      h('div', {class: 'v4-a'}, answer));
  }
  // V5: tse:diag_state carries the asked questions and the pending one with their text; the question itself stays in the
  // copilot chat (15.3), so the pending row only highlights it and a click focuses the composer (onPendingQuestion).
  function v5AskedList() {
    const rows = (state.v5Asked || []).map(a => askedRow(cleanLabel(a.text), a.answer ? cleanLabel(a.answer) : 'No answer given', false));
    const p = state.v5Pending;
    if (p && p.text) {
      const row = askedRow(cleanLabel(p.text), 'Waiting for the answer in the chat', true);
      row.classList.add('v4-pending-q');
      row.setAttribute('data-question-id', p.question_id || '');
      const go = h('button', {type: 'button', class: 'v4-small v4-answer-in-chat', onclick: () => fire('onPendingQuestion', p.question_id || null)}, 'Answer in the chat');
      row.append(go);
      rows.push(row);
    }
    return rows.length ? h('ol', {class: 'v4-asked'}, rows) : muted('The questions asked in this chat appear here.');
  }
  function askedList() {
    if (Array.isArray(state.v5Asked)) return v5AskedList();
    const ix = treeIndex(), obs = new Set(state.observed || []), now = nowKeys(), rows = [];
    (state.observed || []).filter(isSymptom).forEach(n => rows.push(askedRow('The problem', nodeLabel(n), false)));
    askedKeys().forEach(k => {
      const q = ix.qByKey[k], answers = (q.answers || []).filter(a => obs.has(a.nid)).map(a => cleanLabel(a.label));
      const isNow = now.has(k) && !answers.length;
      rows.push(askedRow(cleanLabel(q.hint), answers.join(', ') || (isNow ? 'Waiting for the answer' : 'No answer given'), isNow));
    });
    (state.observed || []).filter(n => !isSymptom(n) && !ix.factQ[n]).forEach(n => {
      rows.push(askedRow(stepForFact(n) || 'Read from the message', nodeLabel(n), false));
    });
    const step = currentStep();
    if (step && step.text && !ix.qByKey[step.key]) rows.push(askedRow(cleanLabel(step.text), 'Waiting for the result of this check', true));
    return rows.length ? h('ol', {class: 'v4-asked'}, rows) : muted('The questions asked in this chat appear here.');
  }
  function leadsList() {
    const ruledSet = ruledIds();
    const open = cards().filter(c => !c.excluded && !ruledSet.has(c.pid));
    const items = (state.observed || []).filter(n => !isSymptom(n)).map(nid => {
      const sup = open.filter(c => (c.matched || []).some(m => m.nid === nid && (m.seen === 0 || m.wins > 0)))
        .sort((a, b) => (b.post || 0) - (a.post || 0)).slice(0, TOP_LEADS);
      return h('li', null, h('div', {class: 'v4-q'}, nodeLabel(nid)),
        h('div', {class: 'v4-a'}, sup.length ? 'Points to ' + sup.map(c => cleanLabel(c.label) + ' (' + pct(c.post) + '%)').join(', ') : 'Does not point to an open cause'));
    });
    return items.length ? h('ul', {class: 'v4-asked'}, items) : muted('Shown once an answer is given.');
  }
  function topicOf(key) {
    const ix = treeIndex();
    let k = key, hops = 0;
    while (hops++ < MAX_PARENT_HOPS) {
      const e = ix.parentOf[k];
      if (!e || e.parent_type === 'symptom') return k;
      const up = ix.factQ[e.parent];
      if (!up || up === k) return k;
      k = up;
    }
    return k;
  }
  function allQuestions() {
    const ix = treeIndex(), keys = Object.keys(ix.qByKey);
    if (!keys.length) return muted('The questions appear here once they are loaded.');
    const asked = new Set(askedKeys()), now = nowKeys(), groups = new Map();
    keys.forEach(k => {
      const t = topicOf(k);
      if (!groups.has(t)) groups.set(t, []);
      groups.get(t).push(k);
    });
    return h('div', null, [...groups.entries()].map(([topic, list]) => {
      const title = keyText(topic) || OTHER_TOPIC;
      const ul = h('ul', {class: 'v4-qlist'}, list.map(k => {
        const mark = now.has(k) ? 'current' : asked.has(k) ? 'done' : 'pending';
        return h('li', {class: 'v4-step-' + mark}, h('span', {class: 'v4-mark', 'aria-label': mark === 'current' ? 'Asking now' : mark === 'done' ? 'Asked' : 'Not asked'}, MARK[mark]),
          ' ', keyText(k) || OTHER_TOPIC);
      }));
      return section('topic-' + topic, title, ul, {cls: 'v4-mini', count: list.length});
    }));
  }
  function renderQuestions() {
    const wrap = h('div', {class: 'v4-questions'});
    const turnsShown = !!(window.Rhs4QTurns && window.Rhs4QTurns.has());      // V5: server turns carry their own matrices
    const v5NoTable = Array.isArray(state.v5Asked) && !(plan().question_table || []).length && !turnsShown;      // no K1/K2 table in tse:diag_state
    wrap.append(...[v5NoTable ? null : section('order', 'Why these questions', orderTable(), {open: true}),      // append(null) would print the word null
      h('h3', {class: 'v4-h'}, 'What was asked'), askedList(),
      section('leads', 'What the answers lead to', leadsList()),
      section('all-questions', 'All questions', allQuestions(), {count: Object.keys(treeIndex().qByKey).length || undefined})].filter(Boolean));
    return wrap;
  }
  let showAllQuestions = false;
  const num2 = v => Number(v || 0).toFixed(2);
  const ORIGIN_TEXT = {book: 'guide only', learned: 'past conversations only', both: 'guide + past conversations'};
  const PAST_SMOOTH = 2;
  // formula card, one worked line from the top question, then one table whose score cell is also a small bar
  function formulaCard(k1, k2, top) {
    const worked = top ? h('div', {class: 'v4-worked'}, h('b', null, 'Example, top question: '), cleanLabel(top.hint) || keyText(top.key),
      h('div', {class: 'v4-formula'}, num2(k1) + ' × ' + num2(top.confidence) + '  +  ' + num2(k2) + ' × ' + num2(top.usage) + '  =  ',
        h('b', null, num2(top.likelihood)))) : null;
    return h('div', {class: 'v4-formula-card'},
      h('div', {class: 'v4-label'}, 'How the next question is chosen (greedy)'),
      h('div', {class: 'v4-formula'}, 'Score  =  K1 × Guide  +  K2 × Past conversations'),
      h('ul', {class: 'v4-terms'},
        h('li', null, h('b', null, 'Guide'), ' (0 to 1): the share of the open causes the guide links to this question, each weighted by how likely it is. It does not change with learning.'),
        h('li', null, h('b', null, 'Past conversations'), ' (0 to 1): wins ÷ (times seen + ' + PAST_SMOOTH + ') over past conversations that ended at a confirmed cause. It is 0 until conversations are taught, and more recent conversations count more.'),
        h('li', null, h('b', null, 'K1 = ' + num2(k1) + ',  K2 = ' + num2(k2)), ' are the only two settings. ',
          h('button', {type: 'button', class: 'v4-link', onclick: () => focusSection('details', 'settings')}, 'Change them in Details'))),
      h('div', {class: 'v4-rule'}, 'Ask the question with the highest Score. Ties go to the cheaper question, then by name. Questions that cannot tell the open causes apart are never asked.'),
      worked);
  }
  const settingsNow = () => Object.assign({}, DEFAULT_SETTINGS, state.settings || {});
  const RAMP_CASES = [0, 1, 2, 3, 5, 10];
  // what the Past cases term does as one cause is confirmed in more and more cases, using the same smoothing as the engine: wins ÷ (seen + PAST_SMOOTH)
  function k2Ramp(k2) {
    const rows = RAMP_CASES.map(n => { const past = n / (n + PAST_SMOOTH); return h('tr', null, h('td', {class: 'v4-n'}, String(n)), h('td', null, num2(past)), h('td', null, num2(k2 * past))); });
    return h('div', {class: 'v4-ramp'},
      h('div', {class: 'v4-label'}, 'How Past conversations grows as conversations are learned'),
      h('div', {class: 'v4-scroll'}, h('table', {class: 'v4-matrix'},
        h('thead', null, h('tr', null, ['Conversations confirmed at the same cause', 'Past conversations (0 to 1)', 'K2 × Past conversations'].map(t => h('th', {scope: 'col'}, t)))),
        h('tbody', null, rows))),
      muted('Every confirmed conversation raises Past conversations, with smaller steps each time, so one lucky conversation cannot outrank the guide. The example assumes a question that every one of those conversations answered the same way.'));
  }
  function scoreCell(r) {
    const a = Number(r.k1_part || 0), b = Number(r.k2_part || 0), top = Math.max(Number(r.likelihood || 0), 1e-9);
    return h('td', {class: 'v4-scorecell', title: 'Guide part ' + num2(a) + ' + past-conversations part ' + num2(b)},
      h('div', {class: 'v4-sbar', role: 'img', 'aria-label': 'Guide ' + num2(a) + ', past conversations ' + num2(b)},
        h('i', {class: 'v4-sbar-guide', style: 'width:' + (a / top * 100) + '%'}), h('i', {class: 'v4-sbar-past', style: 'width:' + (b / top * 100) + '%'})),
      h('b', null, num2(r.likelihood)));
  }
  // what the per-turn history (rhs4_qturns.js) keeps of this state, and the helpers it draws with
  function qTurnSnap() {
    const asked = nowKeys(), ruledSet = ruledIds();
    if (currentStep()) asked.add(currentStep().key);
    return {plan: plan(), table: plan().question_table || [], k1: plan().k1, k2: plan().k2, taught: plan().taught_cases, asked: [...asked],
      open: cards().filter(c => !c.excluded && !ruledSet.has(c.pid)).length, observed: (state.observed || []).length};
  }
  const qTurnDeps = () => ({h, cleanLabel, keyText, muted, section, plural, num2, scoreCell, formulaCard, k2Ramp, originText: ORIGIN_TEXT,
    rows: MATRIX_ROWS, rerender: () => renderPanel('questions')});
  function orderTable() {
    const all = plan().question_table || [];
    if (!all.length && !(window.Rhs4QTurns && window.Rhs4QTurns.has())) return h('div', {class: 'v4-qscore'}, formulaCard(Number(settingsNow().k1), Number(settingsNow().k2), null), k2Ramp(Number(settingsNow().k2)),
      muted('The question table for this chat appears after the first message.'));
    const perTurn = window.Rhs4QTurns && window.Rhs4QTurns.render(qTurnDeps());   // the per-turn matrix; the static table below is the fallback
    if (perTurn) return perTurn;
    const k1 = Number(plan().k1), k2 = Number(plan().k2), taught = Number(plan().taught_cases || 0);
    const asking = nowKeys();
    if (currentStep()) asking.add(currentStep().key);
    const ranked = all.filter(r => r.rank).sort((a, b) => a.rank - b.rank);
    const waiting = all.filter(r => !r.rank);
    const shown = showAllQuestions ? ranked : ranked.slice(0, MATRIX_ROWS);
    const head = ['#', 'Question', 'Guide × K1', 'Past conversations × K2', 'Score', 'Rank without past conversations'];
    const rowFor = r => {
      const next = r.status === 'next' || asking.has(r.key);
      const was = r.rank_cold && r.rank_cold !== r.rank ? (r.rank_cold > r.rank ? '▲ was #' : '▼ was #') + r.rank_cold : (r.rank_cold ? 'same' : '·');
      return h('tr', {class: next ? 'v4-asking' : ''},
        h('td', {class: 'v4-n'}, String(r.rank)),
        h('th', {scope: 'row', class: 'v4-rowh', title: ORIGIN_TEXT[r.origin] || ''}, cleanLabel(r.hint) || keyText(r.key),
          next ? h('span', {class: 'v4-tag'}, 'Asked next') : null),
        h('td', {title: 'Guide = ' + num2(r.confidence) + ' (linked to ' + r.reach + ' of ' + r.n_open + ' open causes)'}, num2(r.k1_part)),
        h('td', {title: 'Past conversations = ' + num2(r.usage) + (taught ? ' (from ' + num2(r.cases_seen) + ' conversation-weights)' : ' (no past conversations yet)')}, num2(r.k2_part)),
        scoreCell(r),
        h('td', {class: 'v4-muted'}, was));
    };
    return h('div', {class: 'v4-qscore'},
      formulaCard(k1, k2, ranked[0]),
      h('div', {class: 'v4-legend'}, h('i', {class: 'v4-sbar-guide'}), ' from the guide   ', h('i', {class: 'v4-sbar-past'}), ' from past conversations'),
      h('div', {class: 'v4-scroll'}, h('table', {class: 'v4-matrix v4-order'},
        h('thead', null, h('tr', null, head.map((t, i) => h('th', {scope: 'col', class: i === 1 ? 'v4-rowh' : ''}, t)))),
        h('tbody', null, shown.map(rowFor)))),
      ranked.length > MATRIX_ROWS ? h('button', {type: 'button', class: 'v4-small', onclick: () => { showAllQuestions = !showAllQuestions; renderPanel('questions'); }},
        showAllQuestions ? 'Show fewer' : 'Show all ' + ranked.length) : null,
      waiting.length ? muted(plural(waiting.length, 'more question') + ' will be considered after an earlier answer.') : null,
      taught === 0 ? muted('No past conversations yet for these causes, so the score comes from the guide only.')
        : muted(taught + ' past conversation' + (taught === 1 ? '' : 's') + ' counted. The last column shows where each question would rank without them.'),
      k1 === 0 && k2 === 0 ? muted('K1 and K2 are both 0, so every question scores 0. Raise one of them in Details.') : null);
  }

  // ---------- 3b. Learning: how a chat becomes learned data, and what it changes next time ----------
  const flowBox = (n, title, body) => h('div', {class: 'v4-flow-box'}, h('div', {class: 'v4-flow-n'}, String(n)), h('div', null, h('b', null, title), body));
  const flowArrow = text => h('div', {class: 'v4-flow-arrow'}, '↓ ' + (text || ''));
  const causeLabelOf = pid => cleanLabel((causeInfo(pid) || {}).label) || idText(pid);
  function learnStatus() {
    const on = learningOn();
    return h('div', {class: 'v4-learn-status' + (on ? ' on' : '')},
      h('b', null, on ? 'Learning is ON' : 'Learning is OFF'),
      h('div', null, on ? 'Scores use past conversations, and a chat you mark Fixed is added to them.' : 'Scores come from the guide only and nothing is stored.'),
      h('button', {type: 'button', class: 'v4-small', onclick: () => fire('onLearning', !on)}, on ? 'Turn learning off' : 'Turn learning on'));
  }
  function workflowDiagram() {
    return h('div', {class: 'v4-flow'},
      flowBox(1, 'The chat', h('div', {class: 'v4-muted'}, 'You describe the problem, answer questions and run the checks until a likely root cause is found.')), flowArrow('you apply the fix'),
      flowBox(2, 'You click Fixed', h('div', {class: 'v4-muted'}, 'Only then is the conversation kept as solved. "Not fixed yet" records that the fix failed and, if a later cause is fixed, counts this one as a wrong diagnosis; escalating keeps only what was seen, with no win.')), flowArrow('extracted'),
      flowBox(3, 'What is taken from the chat', h('ul', {class: 'v4-terms'},
        h('li', null, 'the symptoms and answers given'), h('li', null, 'the order the questions were asked in, and which answer each followed'),
        h('li', null, 'the cause that turned out right'), h('li', null, 'the fix'))), flowArrow('written to the same graph as the guide'),
      flowBox(4, 'Where it lands', h('ul', {class: 'v4-terms'},
        h('li', null, h('b', null, 'Fact → cause links'), ': every fact is marked seen +1 against each cause that was possible, and wins +1 only against the cause that was right, so wrong links get weaker (recent conversations weigh more, old ones fade)'),
        h('li', null, h('b', null, 'Question order'), ': answer → question links get stronger'),
        h('li', null, h('b', null, 'The cause'), ': conversations +1, so it starts higher next time'),
        h('li', null, h('b', null, 'The fix'), ': tried +1, worked +1'),
        h('li', null, 'A cause you type is saved as a candidate and joins the suggestions after ' + ((state.lastLearned && state.lastLearned.promote_after) || 3) + ' confirmed conversations'))), flowArrow('next chat'),
      flowBox(5, 'Next run', h('div', {class: 'v4-muted'}, 'The "Past conversations" part of each question score rises for questions that pointed at the right cause, so they are asked earlier. Wrong diagnoses lower a cause.')));
  }
  // V5 askedLog rows carry the previous answer's text (parent_label) and the question text; POC rows carry node ids only
  const askedFrom = e => e.parent_label ? cleanLabel(e.parent_label) : e.parent ? nodeLabel(e.parent) : 'the problem';
  const dedupeLog = list => { const seen = new Set(); return list.filter(e => { const k = e.parent + '>' + e.key; if (seen.has(k)) return false; seen.add(k); return true; }); };
  function thisChat() {
    const obs = state.observed || [], log = dedupeLog((state.askedLog || []).filter(e => e.source !== 'evaluate'));
    if (!obs.length) return muted('Start a chat to see what would be kept from it.');
    const d = state.diag, winner = d ? cleanLabel(d.cause) : '';
    return h('div', null,
      muted(d && d.identified ? 'If you click Fixed on "' + winner + '", this is what is kept:' : 'No likely root cause yet. This is what has been seen so far:'),
      h('div', {class: 'v4-label'}, 'Symptoms and answers (' + obs.length + ')'),
      h('ul', {class: 'v4-terms'}, obs.map(n => h('li', null, nodeLabel(n)))),
      d && d.identified ? muted('Each of these ' + obs.length + ' facts would get wins +1 against "' + winner + '", and seen +1 against every cause that was possible.') : null,
      h('div', {class: 'v4-label'}, 'Question order (' + log.length + ')'),
      log.length ? h('ul', {class: 'v4-terms'}, log.map(e => h('li', null, askedFrom(e) + '  →  ' + (keyText(e.key) || cleanLabel(e.text) || OTHER_TOPIC)))) : muted('No questions asked yet.'),
      d ? h('div', null, h('div', {class: 'v4-label'}, 'Cause and fix'), h('div', null, winner), d.fix ? h('div', {class: 'v4-muted'}, 'Fix: ' + cleanLabel(d.fix)) : null) : null);
  }
  function lastKept() {
    const r = state.lastLearned;
    if (!r) return muted('No conversation has been kept in this session yet.');
    const win = r.payload && r.payload.winner;
    const rows = [['Cause', win ? causeLabelOf(win) : ''], ['Conversations for this cause now', r.cases !== undefined ? String(r.cases) : ''], ['Status', r.status || '']]
      .concat((r.trust_changes || []).map(c => ['Track record, ' + cleanLabel(c.label), num2(c.before) + ' → ' + num2(c.after)]));
    return h('dl', {class: 'v4-about'}, rows.filter(x => x[1] !== '').map(([k, v]) => [h('dt', null, k), h('dd', null, v)]));
  }
  function storedNow() {
    const nodes = ((state.graph && state.graph.nodes) || []).filter(n => n.type === 'pattern');
    const total = nodes.reduce((s, n) => s + (n.cases || 0), 0);
    const top = nodes.filter(n => (n.cases || 0) > 0).sort((a, b) => b.cases - a.cases).slice(0, 5);
    return h('div', null, muted(total + ' conversation' + (total === 1 ? '' : 's') + ' in memory across ' + plural(nodes.filter(n => n.cases > 0).length, 'cause') + '.'),
      top.length ? h('ul', {class: 'v4-terms'}, top.map(n => h('li', null, cleanLabel(n.label) + ': ' + n.cases))) : null);
  }
  function nextRun() {
    const rows = (plan().question_table || []).filter(r => r.rank && Number(r.k2_part) > 0).slice(0, 4);
    if (!learningOn()) return muted('Learning is off, so the next questions are chosen from the guide only.');
    if (!rows.length) return muted('No past conversation affects the questions for this chat yet. Mark a chat Fixed and start a new one with the same words to see the difference.');
    return h('div', null, muted('Past conversations are already moving these questions:'),
      h('ul', {class: 'v4-terms'}, rows.map(r => h('li', null, '"' + (cleanLabel(r.hint) || keyText(r.key)) + '": past conversations add ' + num2(r.k2_part) + ', now #' + r.rank +
        (r.rank_cold && r.rank_cold !== r.rank ? ' (would be #' + r.rank_cold + ' without them)' : '')))));
  }
  function renderLearning() {
    const wrap = h('div', {class: 'v4-learning'});
    wrap.append(learnStatus(), h('h3', {class: 'v4-h'}, 'How learning works'), workflowDiagram(),
      h('h3', {class: 'v4-h'}, 'This chat'), thisChat(), h('h3', {class: 'v4-h'}, 'Last conversation kept'), lastKept(),
      h('h3', {class: 'v4-h'}, 'Stored now'), storedNow(), h('h3', {class: 'v4-h'}, 'Next run'), nextRun());
    return wrap;
  }

  // ---------- 4. Details ----------
  function recordTurn() {
    const t = state.trace, obsLen = (state.observed || []).length;
    if (prevObserved && !obsLen) { turnLog = []; turnSig = null; turnCount = 0; }
    prevObserved = obsLen;
    if (!Array.isArray(t) || !t.length) return;
    if (t.some(x => typeof x !== 'string')) {
      turnLog = t.map((x, i) => ({n: x.turn || i + 1, lines: Array.isArray(x) ? x : (x.lines || x.trace || [])})).slice(-MAX_TURNS);
      turnCount = turnLog.length;
      return;
    }
    const sig = state.turn !== undefined && state.turn !== null ? 'turn:' + state.turn : JSON.stringify(t);
    if (sig === turnSig) {
      if (turnLog.length) turnLog[turnLog.length - 1].lines = t.slice();
      return;
    }
    turnSig = sig;
    turnLog.push({n: ++turnCount, lines: t.slice()});
    if (turnLog.length > MAX_TURNS) turnLog.shift();
  }
  function plainTrace(line) {
    const s = String(line);
    let m = TRACE_RECORDED.exec(s);
    if (m) return 'Recorded: ' + cleanLabel(m[1]);
    m = TRACE_CONFIDENCE.exec(s);
    if (m) return 'Confidence among the open causes: ' + m[1];
    if (TRACE_NO_SHORTCUT.test(s)) return 'The problem alone was not enough to name a cause, so questions come first.';
    m = TRACE_SOURCES.exec(s);
    if (m) return 'The answer cites ' + m[1] + ' of ' + plural(Number(m[2]), 'source') + ' and shows ' + plural(Number(m[3]), 'screenshot') + '.';
    return DEV_WORDS.test(s) ? null : cleanLabel(s) || null;
  }
  function turnLogBody() {
    if (!turnLog.length) return muted('Each turn is listed here once the chat starts.');
    return turnLog.map(t => {
      const lines = t.lines.map(plainTrace).filter(Boolean);
      return section('turn-' + t.n, 'Turn ' + t.n, lines.length ? h('ol', {class: 'v4-trace'}, lines.map(l => h('li', null, l)))
        : muted('Only internal steps this turn.'), {cls: 'v4-mini'});
    });
  }
  function renderDetails() {
    const wrap = h('div', {class: 'v4-details'});
    wrap.append(...[turnLog.length ? section('turnlog', 'Turn log', turnLogBody(), {count: turnLog.length}) : null,
      (state.mappings || []).length ? section('mappings', 'Hybrid cause matching', mappingsBody(), {count: state.mappings.length}) : null,
      state.devMode ? section('stored', 'Stored data', storedBody()) : null,
      section('about', 'About this diagnosis', about())].filter(Boolean));
    return wrap;
  }
  // V5: every hybrid cause -> diagnosis cause matching call of this diagnosis (tse:diag_state mappings)
  function mappingsBody() {
    return h('ul', {class: 'v4-terms'}, (state.mappings || []).map(m => h('li', null,
      h('b', null, cleanLabel(m.hybrid_label)), ' → ', m.matched ? cleanLabel(m.closest_label || '') : 'no match',
      h('span', {class: 'v4-muted'}, ' (score ' + Number(m.score || 0).toFixed(2) + ', needs ' + Number(m.threshold || 0).toFixed(2) + (m.unpinned ? ', unpinned' : '') + ')'))));
  }
  function storedBody() {
    const refresh = h('button', {type: 'button', class: 'v4-small', onclick: () => { neoLoaded = false; loadNeo(); }}, 'Refresh');
    if (!neoLoaded) neoHost.textContent = '';
    return h('div', null, muted('What the demo database holds for each possible cause, read when opened.'),
      section('stored-tech', TECH_TITLE, h('div', null, h('div', {class: 'v4-actions'}, refresh), neoHost), {cls: 'v4-mini', onOpen: loadNeo}));
  }
  function loadNeo() { if (!neoLoaded && window.NeoView) { neoLoaded = true; window.NeoView.render(neoHost); if (window.Rhs4LodPick) window.Rhs4LodPick.watch(neoHost, () => state); } }      // view-only line filter (rhs4_lodpick.js)
  function about() {
    const info = state.info || {};
    const nodes = (state.graph && state.graph.nodes) || [];
    const cases = nodes.filter(n => n.type === 'pattern').reduce((s, n) => s + (n.cases || 0), 0);
    const usage = state.usage || {};
    const rows = [['Category', info.category], ['Diagnosis', state.mapKey && state.mapKey.diagnosis_id], ['Update', state.mapKey && String(state.mapKey.seq)],
      ['Conversations in memory', cases ? String(cases) : ''], ['Turns', usage.turns !== undefined ? String(usage.turns) : '']];
    return h('dl', {class: 'v4-about'}, rows.filter(r => r[1]).map(([k, v]) => [h('dt', null, k), h('dd', null, cleanLabel(v))]));
  }

  // ---------- public API ----------
  function ensureHost(el) {
    const target = el || host || document.getElementById(HOST_ID);      // V5: the mounted host need not carry the POC id
    if (!target) return false;
    if (target !== host) { host = target; built = false; }
    if (!built) build();
    return true;
  }
  function render(next, cbs) {
    if (cbs) callbacks = cbs;
    state = next || {};
    recordTurn();
    // V5: tse:diag_state `turns` is the history when the server sends it; the browser-kept record is the fallback for older payloads
    if (window.Rhs4QTurns) { if (Array.isArray(state.v5Turns) && window.Rhs4QTurns.load) window.Rhs4QTurns.load(state.v5Turns); else window.Rhs4QTurns.record(qTurnSnap()); }
    if (window.Rhs4CauseDetail) window.Rhs4CauseDetail.noteState(state);   // publish, fix outcome, undo: cached write-ups are dropped   // every state, not only when the Questions tab is drawn, so no turn is missed
    noteTrust();
    if (!ensureHost()) return;
    if (state.neoStale) neoLoaded = false;
    TABS.forEach(t => dirty.add(t.id));
    renderPanel(tab);
  }
  function mount(el, cbs) { if (cbs) callbacks = cbs; ensureHost(el); }
  // New chat: nothing of the previous chat may stay on screen (map, map cache, ruled-out order, turn log)
  function reset() {
    dropMap(); mapCache.clear(); mapLast = null; mapSeq++;
    if (window.DiagMapLayout) window.DiagMapLayout.resetRequest();
    turnLog = []; turnSig = null; turnCount = 0; prevObserved = 0;
    if (window.Rhs4QTurns) window.Rhs4QTurns.reset();
    if (window.Rhs4Explain) window.Rhs4Explain.reset();
    if (window.Rhs4CauseDetail) window.Rhs4CauseDetail.reset();
    if (window.Rhs4Lod) window.Rhs4Lod.reset();      // no line chip stays selected from the previous chat or category
  }
  function focusSection(tabId, key) {
    if (!ensureHost()) return;
    selectTab(TABS.some(t => t.id === tabId) ? tabId : tab, false);
    if (!key) return;
    const esc = (window.CSS && typeof window.CSS.escape === 'function') ? window.CSS.escape(key) : String(key).replace(/["\\]/g, '\\$&');
    const target = panels[tab].querySelector('[data-key="' + esc + '"]');
    if (!target) return;
    if (target.tagName === 'DETAILS') target.open = true;
    if (typeof target.scrollIntoView === 'function') target.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    const f = target.tagName === 'DETAILS' ? target.querySelector('summary') : target;
    if (f) f.focus();
  }
  window.Rhs4 = {render, mount, reset, focus: focusSection, cleanLabel, label: nodeLabel};
})();
